import { DocumentStatus } from '@shared/types';
export { DocumentStatus };

/**
 * Picklist Status - mirrors CSC_CORE PicklistStatusEnum
 */
export enum PicklistStatus {
  PendingFulfillment = 0,
  PartiallyFulfilled = 1,
  Fulfilled = 2,
}

/**
 * NetSuite item tracking type — mirrors CSC_CORE.Features.Receiving.Enums.ReceivingItemTypeEnum.
 * Sourced from PurchaseOrderItem.ItemType (populated by PO sync from item.isserialitem /
 * item.islotitem). Drives the "Serialized" / "Lot" badge in the picklist detail.
 */
export enum ReceivingItemType {
  InventoryItem = 0,
  LotNumbered = 1,
  Serialized = 2,
}

/**
 * Picking History - mirrors CSC_CORE PickingHistoryDto
 */
export interface PickingHistoryDto {
  id: number;
  picklistItemId: number;
  quantity: number;
  unitOfMeasure: string;
  binCode: string;
  binId: number;
  locationId: number;
  inventoryNumberId: number | null;
  pickedById: number;
  pickedByName: string;
  pickedAt: string;
  notes: string | null;
  reading: number | null;
}

/**
 * Bin location info with NetSuite internal IDs
 */
export interface BinInfoDto {
  /**
   * PicklistItemBin row id (86d357xee Phase 1). Zero on freshly-allocated bins
   * not yet persisted; non-zero when loaded from the server.
   */
  id: number;
  binId: number;
  binNumber: string;
  locationId: number;
  inventoryNumberId: number | null;
  inventoryNumber: string | null;
  /** Swap provenance (86d3nauca) — the allocated (frozen) serial + bin; the live values above are the
   *  picked/issued serial + actual source bin. Swap = allocatedInventoryNumberId != null && != inventoryNumberId.
   *  Null on non-swapped and legacy rows. */
  allocatedInventoryNumberId: number | null;
  allocatedInventoryNumber: string | null;
  allocatedBinId: number | null;
  allocatedBinNumber: string | null;
  swapReason: string | null;
  allocatedQuantity: number;
  /**
   * Cumulative quantity from this bin already posted to NetSuite (86d357xee Phase 1).
   * Used by the Adjust dialog as a locked floor and by the MIS post form to
   * skip already-consumed serials in the consumption loop.
   */
  postedQuantity: number;
  /**
   * Server-computed net picked quantity from this bin right now, accounting for
   * `PicklistAdjustmentLog` reductions. Use this (not `allocatedQuantity`) for
   * "currently picked" displays and the Adjust dialog's old-value initialization.
   * See ticket 86d32j03t.
   *
   * `allocatedQuantity` is a CAP, not a record of picks, and it can change after creation (PT-1). On a line's
   * own slot (`items[].bins`) the pick-time rebalance and a Move shift unpicked room between sibling lines, and
   * an Add Items top-up raises it — never below the slot's picked quantity. On a display bin
   * (`itemGroups[].bins`) it is the sum over the group's slots at that `(binId, inventoryNumberId)`, or the one
   * slot's own figure on a serial display bin, which only Add Items raises.
   */
  currentlyPickedQuantity: number;
  /**
   * PW-01 — units of this slot handed to an open posting batch.
   *
   * Populated for **serial-tracked lines only**; `0` elsewhere, where it means "no per-slot figure is
   * published", not "nothing is committed" — a serial post names the slot of every unit in its frozen
   * payload, and no other tracking type has a consumer for a derived split. Read the line-level
   * `postingQuantity` for everything else.
   */
  postingQuantity: number;
  /** PW-01 — the posting batches holding this slot's units, ascending. Empty when nothing is committed. */
  postingBatchIds: number[];
  /**
   * PT-1 — on a DISPLAY bin, the (line, slot) pairs it sums. Mirrored so the shape matches the wire; the
   * detail screens never draw it (the split across MRS lines is provisional). Empty on a line's own slots.
   */
  lines?: BinInfoLineDto[];
  /**
   * PT-2 — set only on an unpicked serial slot whose serial another picklist picked or posted. Render
   * `pickedAtLabel` as sent; never format `pickedAt` on the client, and never show a person's name.
   */
  takenBy?: TakenByDto | null;
}

/** PT-1 — one (line, slot) pair behind a display bin. Mirrors CSC_CORE `BinInfoLineDto`. */
export interface BinInfoLineDto {
  picklistItemId: number;
  picklistItemBinId: number;
  materialRequestItemId: number;
  currentlyPickedQuantity: number;
  postedQuantity: number;
}

/** PT-2 — which other picklist took a serial. Mirrors CSC_CORE `TakenByDto`. */
export interface TakenByDto {
  picklistNumber: string;
  /** UTC ISO. Not for display: render `pickedAtLabel`, the server's one relative-time phrase. */
  pickedAt: string;
  pickedAtLabel: string;
}

/**
 * MIS reference - mirrors CSC_CORE MisReferenceDto.
 * misTranId is null when the post-create tranId fetch failed; UI falls back to "MIS #{misId}".
 */
export interface MisReferenceDto {
  misId: string;
  misTranId: string | null;
  url: string;
  /**
   * PW-10 — how much of THIS line went out under THIS MIS (the `MIS #4471 · 200 pcs` label).
   * Null on picklist- and MRS-level references, where one MIS spans several lines in possibly
   * different units and no single figure is truthful. Optional so the two picklist-level
   * consumers need no change.
   */
  quantity?: number | null;
}

/** Async MIS posting lifecycle (86d3kwt7h) — mirrors CSC_CORE Issuance PostingStatusEnum. */
export enum PostingStatusEnum {
  Posted = 0,
  Posting = 1,
  PostingFailed = 2,
}

/**
 * An in-flight (Posting) or failed (PostingFailed) MIS post attempt — mirrors CSC_CORE
 * PostingBatchStatusDto. Each renders as its own posting card; Posted batches surface via misReferences.
 */
export interface PostingBatchStatusDto {
  id: number;
  status: PostingStatusEnum;
  misTranId: string | null;
  postingFailedReason: string | null;
  postingClaimedAt: string | null;
  /** 86d3mnuxe FIX 3 — how far the failed attempt got toward NetSuite (PostReach numeric; ≥2 = a
   *  record may exist). Drives the card's Retry-vs-Reconcile label + hides Cancel when reached. */
  postReach: number;
  /** The NetSuite internal id the failed attempt created, when known. */
  netSuiteRecordId: string | null;
}

/**
 * Picklist Item - mirrors CSC_CORE PicklistItemDto.
 *
 * One `PicklistItemDto` is ONE MRS LINE — the record. The item view is `PicklistDto.itemGroups`; never treat
 * an `items[]` row as an item on a screen. Its MRS fields are that line's own values.
 */
export interface PicklistItemDto {
  id: number;
  picklistId: number;
  materialRequestItemId: number;
  /** MRS number this item belongs to (from MaterialRequestItem.MaterialRequest) */
  mrsNumber: string;
  /** Material Request ID for grouping */
  materialRequestId: number;
  requestorFullName: string;
  departmentName: string;
  itemCode: string;
  itemName: string;
  netsuiteItemId: number;
  netsuiteDepartmentId: number;
  netsuiteEndUseId: number;
  /** The end use's display name — mirrors `departmentName` beside `netsuiteDepartmentId`. */
  netsuiteEndUseName: string;
  accountRequestorId: number;
  /**
   * PW-06 — the MRS line's total demand, as DOP last synced it. **Cached, not live**: it moves only on an
   * MRS sync/resync, so copy must not promise live numbers and "raise the MRS release" advice should end
   * with a resync hint. Not the same as `quantityToPick`, which is this picklist's slice of the line.
   */
  mrsRequestedQuantity: number;
  /**
   * PW-06 — everything ever issued against this MRS line, from every picklist and every other system.
   * **Cached, not live.** ⚠️ Never substitute `quantityPosted` for it — that is only this picklist's
   * issuance, so the swap under-reports released quantity and over-reports headroom.
   */
  mrsReleasedQuantity: number;
  quantityToPick: number;
  quantityPicked: number;
  quantityPosted: number;
  /**
   * PW-01 — quantity of this line handed to an open posting batch and awaiting NetSuite confirmation.
   *
   * **Enforced, not advisory.** The server subtracts it from `quantityPicked - quantityPosted` and answers
   * a claim that over-selects with a 400 naming the quantity and the owning batches — before anything is
   * written. Three consequences for the UI: the post route can now answer 400 where only 202/200 were
   * expected, the refusal is per line (so a mixed selection can be refused for one item), and it is a race
   * outcome rather than a user error — the recovery is a refetch, not resubmitting the same body.
   *
   * ⚠️ It can be stuck: with the dispatcher or the job type disabled, jobs sit pending forever with no
   * error, so this figure never clears and those units stay blocked. Cancelling the batch is the escape.
   */
  postingQuantity: number;
  /**
   * PW-01 — the posting batches holding `postingQuantity`, ascending. A list, not a scalar: one line can be
   * open in two batches at once. Empty when nothing is committed.
   */
  postingBatchIds: number[];
  bins: BinInfoDto[];
  unitOfMeasure: string;
  /** NetSuite tracking type — null when no PO history exists for the item. */
  itemType: ReceivingItemType | null;
  isPicked: boolean;
  pickedAt: string | null;
  pickedById: number | null;
  isPosted: boolean;
  postedAt: string | null;
  /** Remarks from MRS line item */
  remarks: string;
  /** All MIS references from every posting batch that included this item, ordered by posting time. */
  misReferences: MisReferenceDto[];
  pickingHistories: PickingHistoryDto[];
  isVoided: boolean;
  voidedQuantity: number;
  voidedAt: string | null;
  voidedById: number | null;
  voidReason: string | null;
}

/**
 * Picker assigned to a picklist - mirrors CSC_CORE PicklistPickerDto
 */
export interface PicklistPickerDto {
  id: number;
  userId: number;
  fullName: string;
}

/** PW-03 — the content of one open posting batch. Mirrors CSC_CORE `PostingBatchContentDto`. */
export interface PostingBatchContentDto {
  postingBatchId: number;
  /** Σ of the lines' quantities. */
  totalQuantity: number;
  /**
   * False when the batch's payload could not be read. `lines` is then empty and `totalQuantity` 0 — render
   * "contents unavailable", never "0 units": the batch is real and is still holding something.
   */
  contentsResolved: boolean;
  lines: PostingBatchLineDto[];
}

/** PW-03 — one line of an open posting batch. */
export interface PostingBatchLineDto {
  picklistItemId: number;
  /** May be empty; fall back to `#{picklistItemId}` rather than rendering a blank chip. */
  itemCode: string;
  itemName: string;
  unitOfMeasure: string;
  /** The quantity frozen at claim — the intent, not an outcome. */
  quantity: number;
}

/**
 * Picklist - mirrors CSC_CORE PicklistDto
 */
export interface PicklistDto {
  id: number;
  picklistNumber: string;
  documentStatus: DocumentStatus;
  picklistStatus: PicklistStatus;
  notes: string | null;
  remarks: string | null;
  fulfilledAt: string | null;
  fulfilledById: number | null;
  isPosted: boolean;
  postedAt: string | null;
  latestMisReference: MisReferenceDto | null;
  /** In-flight + failed MIS post attempts (86d3kwt7h) — each renders as a posting card. */
  activePostingBatches: PostingBatchStatusDto[];
  /** PW-01 — Σ of the items' `postingQuantity`: how much of this picklist is handed to open batches. */
  postingQuantity: number;
  /**
   * PW-03 — what is IN each open batch. Carries no status, reach or NetSuite id on purpose: batch lifecycle
   * comes from the outbox status projection, and holding two copies of it is what made
   * `activePostingBatches` a hazard. Join them by `postingBatchId`.
   *
   * Populated only on the by-id read; `[]` on the list endpoints.
   */
  postingBatchContents: PostingBatchContentDto[];
  createdAt: string;
  createdById: number;
  createdByName: string;
  /** One row per MRS line — the record. Read it only for per-line facts; the item view is `itemGroups`. */
  items: PicklistItemDto[];
  /**
   * PT-1 — the item view: one entry per `(netsuiteItemId, unitOfMeasure)` of each line's MRS line, compared
   * exactly (O-22); an item-0 line is a group of its own; voided lines are left out; ordered by anchor. Rebuilt
   * by the server after every read-side fold, so its posting figures and `takenBy` are current.
   */
  itemGroups: PicklistItemGroupDto[];
  /** PT-1 — by-id read only (`[]` on list reads and on the raw-read mutation answers). */
  mrsSummaries?: PicklistMrsSummaryDto[];
  pickers: PicklistPickerDto[];
  /** Counts item groups (PT-1), not MRS lines. */
  totalItems: number;
  pickedItemsCount: number;
  progressPercentage: number;
  /** Distinct MRS numbers across all items in this picklist */
  mrsNumbers: string[];
}

/**
 * PT-1 — one item on the picklist. Mirrors CSC_CORE `PicklistItemGroupDto`.
 *
 * `id` is the ANCHOR: the lowest line id in the group, stable (new lines get higher ids, Remove takes whole
 * groups). A pick, a Move, a post or Remove Items names a planned line's `id`, never this one alone.
 */
export interface PicklistItemGroupDto {
  id: number;
  netsuiteItemId: number;
  unitOfMeasure: string;
  /** The anchor line's (O-22). */
  itemCode: string;
  itemName: string;
  itemType: ReceivingItemType | null;
  quantityToPick: number;
  quantityPicked: number;
  quantityPosted: number;
  postingQuantity: number;
  /** The union of the lines' open batches, ascending (fold-filled). */
  postingBatchIds: number[];
  isPicked: boolean;
  isPosted: boolean;
  /** One per line, ordered by id. */
  plannedLines: PicklistPlannedLineDto[];
  /** DISPLAY bins: a serial's own slot, or a lot/untracked item's slots merged by (binId, inventoryNumberId). */
  bins: BinInfoDto[];
  /**
   * PT-6 D — the group's Adjust version, opaque. Set on the by-id, Adjust and Replace responses; absent or null on
   * list reads and on the raw answers of Add/Remove Picker. The Adjust dialog echoes it; it never computes one.
   */
  adjustVersion?: string | null;
}

/**
 * PT-1 — one MRS line an item serves. Mirrors CSC_CORE `PicklistPlannedLineDto`. `id` is the line's
 * `PicklistItem.Id`. Two lines are on the same MRS when their `materialRequestId` is equal.
 *
 * On the Closed pages the lines are thin: requestor, department, end use, remarks and MRS figures arrive
 * empty or 0. Read every figure through `?? 0`.
 */
export interface PicklistPlannedLineDto {
  id: number;
  materialRequestItemId: number;
  materialRequestId: number;
  mrsNumber: string;
  requestorFullName: string;
  accountRequestorId: number;
  netsuiteDepartmentId: number;
  /** One value, the cost centre (O-10). */
  departmentName: string;
  netsuiteEndUseId: number;
  netsuiteEndUseName: string;
  remarks: string;
  itemName: string;
  mrsRequestedQuantity: number;
  mrsReleasedQuantity: number;
  /** = the line's quantityToPick. */
  quantityPlanned: number;
  quantityPicked: number;
  quantityPosted: number;
  /** This line's in-flight figure, from frozen payloads (the read-side fold). */
  postingQuantity: number;
  isPicked: boolean;
  isPosted: boolean;
  /** PT-5 — the line's open batches, ascending. Mirrored; the Items row reads the group's union. */
  postingBatchIds?: number[];
  /** PT-5 — by-id read only. Mirrored; not read by the detail's Items table. */
  mrsInFlightQuantity?: number;
}

/**
 * PT-1 — one MRS's standing, for the shared hover card. Mirrors CSC_CORE `PicklistMrsSummaryDto`: figures are
 * summed over every line of the MRS; `pickedNotIssuedQuantity` counts every open picklist (O-17).
 */
export interface PicklistMrsSummaryDto {
  materialRequestId: number;
  mrsNumber: string;
  requestorFullName: string;
  requestedQuantity: number;
  releasedQuantity: number;
  pickedNotIssuedQuantity: number;
  lineCount: number;
  linesFullyIssued: number;
  /** Null when the MRS's lines use different units. */
  unitOfMeasure: string | null;
}

/**
 * Lightweight search result for picklist autocomplete suggestions.
 * Mirrors CSC_CORE PicklistSearchResultDto.
 */
export interface PicklistSearchResultDto {
  id: number;
  picklistNumber: string;
  mrsNumbers: string[];
  totalItems: number;
  createdByName: string;
  createdAt: string;
}

/**
 * Row types for expandable picklist table
 */
export type PicklistTableRow = PicklistParentRow | PicklistItemRow;

export interface PicklistParentRow {
  rowType: 'picklist';
  id: string;
  picklist: PicklistDto;
  subRows: PicklistItemRow[];
}

export interface PicklistItemRow {
  rowType: 'item';
  id: string;
  picklistId: number;
  /** PT-7 D2 — one sub-row per item: the group, not an MRS line. */
  item: PicklistItemGroupDto;
}

/**
 * PW-23 — the landing's two tab counts. Mirrors CSC_CORE `PicklistCountsDto`.
 *
 * Each is scoped exactly like the list endpoint it labels, so a pill can never disagree with the table
 * beneath it: `open` matches `GET picklist/open`, `closed` matches `GET picklist/closed/paged` with no
 * search term.
 */
export interface PicklistCountsDto {
  open: number;
  closed: number;
}

/**
 * Pending quantity data for an item across all unposted picklists.
 * Grouped by NetSuite Item ID (product-level).
 */
export interface ItemPendingQuantityDto {
  netsuiteItemId: number;
  /** Quantity allocated but not yet physically picked */
  pendingPickingQuantity: number;
  /** Quantity picked but not yet posted to NetSuite */
  pendingPostingQuantity: number;
  /** Total committed quantity not yet reflected in NetSuite */
  totalCommittedQuantity: number;
}

/**
 * Custom bin allocation entry for picklist creation.
 */
export interface CreatePicklistBinRequest {
  binId: number;
  binCode: string;
  locationId: number;
  inventoryNumberId: number | null;
  inventoryNumber: string | null;
  allocatedQuantity: number;
}

/**
 * Request DTO for creating a picklist item
 */
export interface CreatePicklistItemRequest {
  materialRequestItemId: number;
  quantityToPick: number;
  /** Optional custom bin allocations. If omitted, server performs auto-allocation. */
  bins?: CreatePicklistBinRequest[];
}

/**
 * Request DTO for creating a picklist
 */
export interface CreatePicklistRequest {
  items: CreatePicklistItemRequest[];
  notes?: string;
}

/**
 * Per-item posting entry for PostPicklistRequest.
 */
export interface PostPicklistItemRequest {
  picklistItemId: number;
  quantityToPost: number;
  /**
   * Per-unit selection list for serial-tracked items (86d357xee Phase 3). Required
   * when the item is serial-tracked; omitted otherwise. Sum of entries with
   * doIssue=true must equal quantityToPost.
   */
  unitsToIssue?: UnitToIssueRequest[];
}

/**
 * One unit selection in a serial-tracked item's post payload.
 */
export interface UnitToIssueRequest {
  /** The PicklistItemBin row id this unit corresponds to. */
  picklistItemBinId: number;
  /**
   * The serial actually being issued. Equal to the pre-allocated value unless
   * MRO staff overrode it in the post-form dropdown.
   */
  inventoryNumberId: number;
  /** Defaults to true. Uncheck to defer this unit to a later Post-MIS call. */
  doIssue: boolean;
  /**
   * Where the swapped-out unit went — REQUIRED when this unit's serial is a swap
   * (inventoryNumberId differs from the pre-allocated bin's). Nullable so the server can
   * distinguish "not provided" from an explicit ReturnedToBin (86d3gymp9). Omitted for
   * non-swap / deferred units.
   */
  disposition?: SerialDisposition | null;
  /** Optional free-text note accompanying the disposition. */
  dispositionNote?: string | null;
  /** Display text of the chosen serial (e.g. "SRM-HW-351") — sent on a swap so the activity feed shows it. */
  newSerialNumber?: string | null;
}

/**
 * PT-2 — one on-hand serial of an item, with its status: the handheld's swap list and PT-6's Swap serial list.
 * Mirrors CSC_CORE `SwapSerialDto`. `status` is a string on purpose (an unknown value degrades, never mismatches).
 */
export interface SwapSerialDto {
  inventoryNumberId: number;
  inventoryNumber: string;
  binId: number;
  binCode: string;
  locationId: number;
  quantityAvailable: number;
  status: SwapSerialStatus | string;
  /** Only when `status` is `Reserved`. */
  reservedBy?: { picklistNumber: string; status: string } | null;
  /** Only when `status` is `PickedElsewhere`. */
  takenBy?: TakenByDto | null;
}

export type SwapSerialStatus = 'Available' | 'OnThisPicklist' | 'PickedHere' | 'Reserved' | 'PickedElsewhere';

/**
 * A serial NetSuite still counts on hand that another open picklist holds (owner, 2026-10-05). Mirrors CSC_CORE
 * `SerialCommitmentDto`. The Allocate Bins dialog hides these by default and never lets one be chosen.
 */
export interface SerialCommitmentDto {
  inventoryNumberId: number;
  inventoryNumber: string | null;
  /** `Reserved` (allocated, unpicked) or `PickedElsewhere` (picked, not yet posted): the swap list's words. */
  status: 'Reserved' | 'PickedElsewhere' | string;
  picklistNumber: string;
}

/** Mirrors CSC_CORE `SerialCommitmentsRequestDto`. */
export interface SerialCommitmentsRequest {
  inventoryNumberIds: number[];
  /** Add Items only: the picklist being added to, whose own serials are not another picklist's. */
  excludePicklistId?: number | null;
}

/**
 * What other open picklists still hold of one bin of an item, allocated less posted (14ykpqtv7nd). Mirrors CSC_CORE
 * `BinPendingAllocationDto`. A lot or untracked item reads `pendingQuantity`; a serialized item's held serials come
 * from `SerialCommitmentDto` instead.
 */
export interface BinHoldDto {
  netsuiteItemId: number;
  binId: number;
  pendingQuantity: number;
  /** The part held by slots that name no inventory number. */
  unpinnedQuantity: number;
}

/** Mirrors CSC_CORE `BinHoldsRequestDto`. */
export interface BinHoldsRequest {
  netsuiteItemIds: number[];
  /** Add Items only: the picklist being added to, whose own slots are not another picklist's. */
  excludePicklistId?: number | null;
}

/** PT-6 E3 — the bin card's Swap serial. Mirrors CSC_CORE `ReplaceSerialRequestDto`. */
export interface ReplaceSerialRequest {
  picklistItemBinId: number;
  /** The serial the dialog showed on the slot; a different one under the lock is a 409. */
  expectedInventoryNumberId: number;
  newInventoryNumberId: number;
  newInventoryNumber: string;
  newBinId: number;
  newBinCode: string;
  newLocationId: number;
}

/**
 * Request DTO for posting a picklist to MIS (Material Issuance Slip).
 */
export interface PostPicklistRequest {
  picklistId: number;
  items: PostPicklistItemRequest[];
  reasonForAdjustmentId: number;
  itemMemos?: Record<number, string>;
  itemReadings?: Record<number, number | null>;
  requestedByEmployeeId: string;
  /**
   * PW-25 — the display name of the employee chosen above, exactly as the picker rendered it. The MIS
   * slip has to name a person, and the id alone would force a NetSuite employee lookup per slip open to
   * display a historical fact that cannot change. Sending it snapshots what the poster actually saw.
   * Optional so an older client that omits it still posts.
   */
  requestedByName?: string;
  /**
   * E7 item 3 — per-dialog idempotency key (a GUID). Means "I do not know whether my previous claim
   * landed", so every re-submit within one open post dialog sends the SAME value and a reopened dialog
   * mints a new one. Without it a lost response (dropped connection, reload) makes the re-click a second
   * partial batch, and therefore a second NetSuite Inventory Adjustment against the same stock.
   * Optional so the server keeps its back-compat path.
   */
  clientKey?: string;
}

/**
 * Request DTO for adding items to an existing picklist.
 */
export interface AddPicklistItemsRequest {
  items: CreatePicklistItemRequest[];
}

/**
 * Request DTO for removing items from an existing picklist.
 */
export interface RemovePicklistItemsRequest {
  picklistItemIds: number[];
}

/**
 * The adjustment log's type. Numeric values mirror CSC_CORE `PicklistAdjustmentTypeEnum`. Read-only on the web:
 * the Adjust v2 request carries no type (PT-6 B).
 */
export enum PicklistAdjustmentType {
  QuantityEdit = 0,
  UndoPick = 1,
  /** A serial slot's identity changed; since PT-6 written by the bin card's Swap serial. */
  PostTimeSerialOverride = 2,
  /** PT-6 — units counted for one MRS line were counted for another. */
  Move = 3,
}

/**
 * Int values 3, 4, 5 are intentionally skipped — they belonged to
 * IssuedInError / ReturnedByRequestor / CountError, removed before
 * user-facing rollout. Gap preserved (vs renumbering) so any historical
 * rows that carry those ints don't silently re-map.
 */
export enum PicklistAdjustmentReason {
  WrongItem = 0,
  WrongQuantity = 1,
  WrongBin = 2,
  Other = 6,
  /** PT-6b — a Move's fixed reason. Nobody chooses it: the Move dialog sends it, and Adjust's chips never offer it. */
  WrongMrs = 7,
}

/**
 * Where units went (86d3gymp9), recorded for audit: on an Adjust lowering whose units were not given to another MRS
 * line (Returned to bin, Damaged, Missing), and on a bin-card Swap serial (Taken elsewhere). Numeric values mirror
 * CSC_CORE `SerialDispositionEnum`. The actionable worklist is deferred (86d3mxh47).
 */
export enum SerialDisposition {
  ReturnedToBin = 0,
  Damaged = 1,
  Missing = 2,
  /** PT-2/PT-6 — a swap's outgoing serial went to another picklist. Never a lowering's answer. */
  TakenElsewhere = 3,
}

/** Human labels for the SerialDisposition values. */
export const SERIAL_DISPOSITION_LABELS: Record<SerialDisposition, string> = {
  [SerialDisposition.ReturnedToBin]: 'Returned to bin',
  [SerialDisposition.Damaged]: 'Damaged',
  [SerialDisposition.Missing]: 'Missing',
  [SerialDisposition.TakenElsewhere]: 'Taken elsewhere',
};

/**
 * One row of the posting-allocation breakdown (86d3n1xum) — what a picklist line actually issued to MIS,
 * per (bin, serial/lot), attributed to its MIS batch. Mirrors CSC_CORE PostingBreakdownRowDto. A line can
 * produce several rows (FEFO splits a bin across lots; partial posts split across MIS batches).
 */
export interface PostingBreakdownRow {
  picklistItemId: number;
  misId: string | null;
  misTranId: string | null;
  misUrl: string;
  postedAt: string | null;
  postedByName: string | null;
  /**
   * PW-26 — the MIS header fields as SENT for this batch (persisted by PW-25). Null on batches that
   * predate it or carried none; the slip shows an em-dash rather than a plausible-looking substitute.
   */
  reasonForAdjustmentName: string | null;
  requestedByName: string | null;
  binCode: string;
  /** The ISSUED serial/lot id (post-FEFO / post-swap). */
  issuedInventoryNumberId: number | null;
  /** The ISSUED serial/lot text (ledger snapshot, else movement fallback, else null → show id). */
  issuedInventoryNumber: string | null;
  /** The PICKED serial/lot id on the slot (legacy basis). */
  pickedInventoryNumberId: number | null;
  /** The ALLOCATED serial id (86d3nauca) — the swap badge flags issued != allocated (a pick-time swap
   *  makes issued == picked, so the old issued-vs-picked basis no longer detects it). Null on non-serial/legacy. */
  allocatedInventoryNumberId: number | null;
  quantity: number;
  expirationDate: string | null;
  disposition: SerialDisposition | null;
}

/**
 * PT-6 B — the Adjust v2 request. One item group; quantity rows OR one move, never both. A row is a line and its own
 * slot; a disposition names a display bin (a serial display bin is its slot). Mirrors CSC_CORE
 * `AdjustPicklistItemRequestDto`.
 */
export interface AdjustPicklistItemRequest {
  /** The group's `adjustVersion` captured when the dialog opened. Never empty (the server refuses it). */
  version: string;
  reason: PicklistAdjustmentReason;
  notes?: string | null;
  rows: { picklistItemId: number; picklistItemBinId: number; newQuantity: number }[];
  moves: { fromPicklistItemId: number; picklistItemBinId: number; toPicklistItemId: number; quantity: number }[];
  /** The display bin's id. Returned to bin, Damaged or Missing only. */
  dispositions: { picklistItemBinId: number; disposition: SerialDisposition }[];
}

/**
 * Adjustment reason from NetSuite lookup.
 */
export interface AdjustmentReasonDto {
  id: number;
  name: string;
  accountId: number;
}
